import { computed, reactive, type ComputedRef, type Ref, type UnwrapNestedRefs } from 'vue';
import { syncGeoItemWithParent } from 'src/lib/geoItemHelper';
import { applyMapPinPosition } from 'src/lib/moviePlayerHelper';
import type { GeoItemMeta, GeoItemsMeta, GIComment, GIMovie } from 'src/models/geoItem';
import type { IExtremeMapRef, IMoviePlayerVideo, Location, MapElemInfo } from 'src/models';
import type EMPinLayerManager from 'src/lib/extreme_map/EMPinLayerManager';
import type { MoviePlayerState } from 'src/models/moviePlayer';

// 現在再生位置を示す地図ピンのレイヤー内ID。
const MAP_PIN_ID = 'map-pin1';
// コメント新規作成時に地図上へ表示するアイコン画像パス。実行中に変化しない値のためモジュール定数として定義する。
const MAP_ELEM_ICON_PATH = '/static/img/comment_icon_01.png';

interface Deps {
  refExtremeMap: Ref<IExtremeMapRef | undefined>;
  refMoviePlayerVideos: Ref<IMoviePlayerVideo[] | undefined>;
  parentGeoItemsMeta: GeoItemsMeta;
  setCurrentMovie: (movie: GIMovie, movieIdx: number, playOffsetMsec: number) => Promise<void>;
  pauseUI: () => void;
}

interface MoviePlayerMapLinkageContext {
  mapElemIconPath: string;
  prepareMap: () => Promise<void>;
  updateMapPinPosition: (location: { lat: string; lon: string }) => void;
  seekByMapClick: (clickedLocation: Location) => Promise<void>;
  onMapItemClicked: () => void;
  onMapAllDeselected: () => void;
  startCreateMapElem: () => Promise<void>;
  createMapElem: (obj: MapElemInfo<GIComment>) => Promise<void>;
  visibleGeoItemLayers: ComputedRef<GeoItemMeta[]>;
  updateMap: () => Promise<void>;
}

export function useMoviePlayerMapLinkage(
  state: UnwrapNestedRefs<MoviePlayerState>,
  deps: Deps,
): UnwrapNestedRefs<MoviePlayerMapLinkageContext> {
  const mapElemIconPath = MAP_ELEM_ICON_PATH;
  const prepareMap = async() => {
    // reactive()のUnwrapNestedRefsで型が変換されているため、showDataLayerが要求する元のGeoItemMeta型へ戻す
    const metaItem = state.geo.geoItemsMeta.map.pin as GeoItemMeta;
    const point = { id: MAP_PIN_ID, location: { lat: 0, lon: 0 } };
    if (deps.refExtremeMap.value) {
      deps.refExtremeMap.value.showDataLayer(metaItem, [point]);
    }
  };
  const updateMapPinPosition = ({ lat, lon }: { lat: string; lon: string }) => {
    // pinレイヤーのlayerManagerには初期化時にEMPinLayerManagerが設定されるため、抽象型から具象型へ絞り込む
    const layerMgr = state.geo.geoItemsMeta.map.pin.layerManager as EMPinLayerManager;
    applyMapPinPosition(layerMgr, MAP_PIN_ID, lat, lon);
  };
  const seekByMapClick = async(clickedLocation: Location) => {
    state.playback.isSeeking = true;
    if (!deps.refExtremeMap.value || !state.movieList.listSelection.currentMovieList) {
      state.playback.isSeeking = false;
      return;
    }
    const mgi = deps.refExtremeMap.value.getClosestMgi(clickedLocation);
    if (!mgi) { state.playback.isSeeking = false; return; }
    // seekByTimeTextと同じロジックの方がきれいだが、
    // ルート検索だと適用できないので、これで.
    let targetMovie;
    let targetMovieIdx;
    let targetMoviePlayOffsetMsec = 0;
    for (let i = 0, len = state.movieList.listSelection.currentMovieList.movies.length; i < len; i++) {
      const movie = state.movieList.listSelection.currentMovieList.movies[i];
      if (movie.id === mgi.movieId) {
        targetMovie = movie;
        targetMovieIdx = i;
        targetMoviePlayOffsetMsec = (mgi.startMsecDiff ?? 0) - (movie.playStartOffsetMsec || 0);
        break;
      }
    }
    if (!targetMovie || targetMovieIdx === undefined) { state.playback.isSeeking = false; return; }
    await deps.setCurrentMovie(
      targetMovie,
      targetMovieIdx,
      targetMoviePlayOffsetMsec,
    );
    updateMapPinPosition({ lat: mgi.location.lat, lon: mgi.location.lon });
    state.playback.isSeeking = false;
  };
  const onMapItemClicked = () => {
    // TODO
  };
  const onMapAllDeselected = () => {
    // TODO
  };
  const startCreateMapElem = async() => {
    deps.pauseUI();
    if (!deps.refMoviePlayerVideos.value) return;

    const promises = deps.refMoviePlayerVideos.value.map(async(elem) => {
      return elem.getScreenshotBlob();
    });
    const screenshots = await Promise.all(promises);
    if (!state.geo.currentMgi) return;

    const candidateImages: Blob[] = [];
    screenshots.forEach(e => {
      if (e instanceof Blob) {
        candidateImages.push(e);
      }
    });
    state.geo.mapElemInfo = {
      dataName: 'new',
      data: {
        lat: state.geo.currentMgi.location.lat,
        lon: state.geo.currentMgi.location.lon,
        kpUid: state.geo.currentMgi.kpUid,
      },
      candidateImages,
    };
  };
  const createMapElem = async(obj: MapElemInfo<GIComment>) => {
    if (!deps.parentGeoItemsMeta.map[obj.dataName]) {
      console.warn('createMapElem. Unknown resource type', obj.dataName);
      return;
    }
    if (!state.geo.currentMgi) return;

    // 保存ボタン押下時の位置を登録
    obj.data.location = {
      lat: parseFloat(state.geo.currentMgi.location.lat),
      lon: parseFloat(state.geo.currentMgi.location.lon),
    };
    const metaItem = deps.parentGeoItemsMeta.map[obj.dataName];
    try {
      const resource = await metaItem.manager.createResource(obj.data);
      if (deps.parentGeoItemsMeta.show[obj.dataName]) {
        metaItem.layerManager.addLayerItem(resource);
      }
      state.geo.mapElemInfo = null;
      if (deps.refExtremeMap.value) {
        syncGeoItemWithParent(
          // reactive()のUnwrapNestedRefsで型が変換されているため、元のGeoItemsMeta型へ戻す
          state.geo.geoItemsMeta as GeoItemsMeta,
          deps.parentGeoItemsMeta,
          deps.refExtremeMap.value,
        );
      }
    } catch (e) {
      state.geo.createMapElemFailed++;
    }
  };
  const visibleGeoItemLayers = computed(() => {
    return [deps.parentGeoItemsMeta.map['comment']];
  });
  const updateMap = async() => {
    const ml = state.movieList.listSelection.currentMovieList;
    if (!deps.refExtremeMap.value || !ml) return;

    deps.refExtremeMap.value.refreshMovieLayer([ml]);
    deps.refExtremeMap.value.fitToMovieListsExtent([ml]);
  };

  return reactive({
    prepareMap,
    updateMapPinPosition,
    seekByMapClick,
    onMapItemClicked,
    onMapAllDeselected,
    startCreateMapElem,
    createMapElem,
    visibleGeoItemLayers,
    updateMap,
    mapElemIconPath,
  });
}
